import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Home, Building, Calendar, MapPin, Phone, Mail, Users, Shield, Star, Building2, TrendingUp, Search, FileText, Eye, CheckCircle, CreditCard, ChevronRight, Facebook, Twitter, Instagram, Youtube, User, AlertCircle, Info, ArrowRight, Download } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';
import AvailabilityModal from '../../components/modals/AvailabilityModal';
import { transformVenuesForUI, UIVenue } from '../../utils/venueTransformer';
import {
  getPrefixSuffixRatePerDay,
  getSecurityDeposit,
  getSecurityDepositPercent,
  getGstRate,
} from '../../utils/venueCharges';
import { useActiveVenues } from '../../hooks/useVenues';
import axios from 'axios';
import useBlockedDates from '../../hooks/useBlockedDates';
import BookingModal from '../../components/modals/Bookingmodal';


const theme = {
  secondaryBlue: '#0066CC',
  accentPurple: '#6B2D8C',
  govBlue: '#003B7A',
  white: '#FFFFFF',
  cream: '#FFF9F5',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444'
};

interface FormErrors {
  from?: string;
  to?: string;
  eventName?: string;
  applicantName?: string;
  mobile?: string;
  email?: string;
}

const useIntersectionObserver = (threshold = 0.2) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold }
    );
    const currentRef = ref.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);
  
  return [ref, isVisible];
};

interface AnimatedCounterProps {
  end: number;
  duration?: number;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [ref, isVisible] = useIntersectionObserver(0.2);
  
  useEffect(() => {
    if (!isVisible) return;
    let start = 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(Math.floor(end));
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [end, duration, isVisible]);
  
  return <span ref={ref as unknown as React.RefObject<HTMLSpanElement>}>{count.toLocaleString()}</span>;
};

const Loader = () => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${theme.govBlue}, ${theme.accentPurple})`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    color: theme.white
  }}>
    <div style={{
      width: '80px',
      height: '80px',
      border: `6px solid ${theme.white}40`,
      borderTop: `6px solid ${theme.white}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <h2 style={{ marginTop: '24px', fontSize: '1.8rem', fontWeight: '700' }}>HMDA Booking Portal</h2>
    <p style={{ opacity: 0.9 }}>Loading...</p>
  </div>
);

const HMDAPortal = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [language, setLanguage] = useState('English');
  const [fontSize, setFontSize] = useState('normal');
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<any>(null);
  const [showPlazaNotice, setShowPlazaNotice] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookingDates, setBookingDates] = useState({ from: '', to: '' });
  const [prefixDays, setPrefixDays] = useState(0);
  const [suffixDays, setSuffixDays] = useState(0);
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [trackingData, setTrackingData] = useState({ bookingNumber: '', mobile: '' });
  const [bookingResults, setBookingResults] = useState<any>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [trackError, setTrackError] = useState('');
  const [downloadingNumber, setDownloadingNumber] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [availabilityVenue, setAvailabilityVenue] = useState<any>(null);
  const [availabilityMonth, setAvailabilityMonth] = useState(new Date());
  const [allBookings, setAllBookings] = useState<any[]>([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState<any>(null);
  const [formErrors, setFormErrors] = useState<any>({});


  // API Base URL - Update this to your backend URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://bpphmdademo.alliantprojects.com/api/v1';

  // Download a booking's receipt PDF straight from a track-result card.
  // Track results only carry the bookingNumber, so first resolve the internal id.
  const handleDownloadReceipt = async (bookingNumber: string) => {
    if (!bookingNumber) return;
    setDownloadingNumber(bookingNumber);
    try {
      const lookup = await axios.get(
        `${API_BASE_URL}/bookings/public/receipt/${encodeURIComponent(bookingNumber)}`
      );
      const bookingId = (lookup.data?.data || lookup.data)?.id;
      if (!bookingId) {
        alert('Receipt not found for this booking.');
        return;
      }
      const pdf = await axios.get(
        `${API_BASE_URL}/bookings/public/${bookingId}/receipt/pdf`,
        { responseType: 'blob', timeout: 30000 }
      );
      const url = window.URL.createObjectURL(new Blob([pdf.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `HMDA_Receipt_${bookingNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Failed to download receipt:', error);
      if (error?.code === 'ECONNABORTED') {
        alert('Request timed out. Please try again.');
      } else if (error?.response?.status === 404) {
        alert('Receipt not found for this booking.');
      } else {
        alert('Failed to download receipt. Please try again or contact support.');
      }
    } finally {
      setDownloadingNumber('');
    }
  };

  const [formData, setFormData] = useState({
    eventName: '',
    applicantName: '',
    mobile: '',
    email: '',
    gstNumber: ''
  });

  const getMonthDays = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

 



  // Add this useEffect to debug image loading
useEffect(() => {
  // Debug logo loading
  const logoImg = new Image();
  logoImg.src = `${process.env.PUBLIC_URL}/images/hmdalogo.png`;
  logoImg.onload = () => {
    console.log('✅ HMDA logo loaded successfully');
    console.log('Logo dimensions:', logoImg.width, 'x', logoImg.height);
  };
  logoImg.onerror = (error) => {
    console.error('❌ HMDA logo failed to load:', error);
    console.log('Attempted path:', logoImg.src);
    console.log('Current origin:', window.location.origin);
  };
}, []);
  const calculateTotal = () => {
    if (!selectedVenue || !bookingDates.from || !bookingDates.to) 
      return { rent: 0, prefixSuffixRent: 0, securityDeposit: 0, gst: 0, total: 0, entryFee: 0 };
    
    const fromParts = bookingDates.from.split('-');
    const from = new Date(parseInt(fromParts[0]), parseInt(fromParts[1]) - 1, parseInt(fromParts[2]));
    
    const toParts = bookingDates.to.split('-');
    const to = new Date(parseInt(toParts[0]), parseInt(toParts[1]) - 1, parseInt(toParts[2]));
    
    const days = Math.ceil((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    
    let baseRent = 0;
    let entryFee = 0;

    if (selectedVenue.type === 'event') {
      baseRent = selectedVenue.rate * days;
    } else {
      baseRent = selectedVenue.rate;

      if (selectedVenue.name === 'Lumbini Park' && selectedVenue.entryFee) {
        entryFee = selectedVenue.entryFee;
      }
    }

    // Deposit is either flat or a percentage of the rent, so it needs baseRent.
    const securityDeposit = getSecurityDeposit(selectedVenue, baseRent);

    const prefixSuffixRent = (prefixDays + suffixDays) * getPrefixSuffixRatePerDay(selectedVenue);
    const totalRent = baseRent + prefixSuffixRent;
    const gst = totalRent * getGstRate(selectedVenue);
    const total = totalRent + securityDeposit + gst + entryFee;
    
    return { 
      rent: baseRent, 
      prefixSuffixRent, 
      securityDeposit, 
      gst, 
      entryFee,
      total 
    };
  };

  const validateForm = () => {
    console.log('=== VALIDATION DEBUG ===');
    console.log('bookingDates:', bookingDates);
    console.log('formData:', formData);

    const errors: FormErrors = {};

    if (!bookingDates.from) {
      console.log('❌ From date missing');
      errors.from = 'Booking start date is required';
    }
    
    if (!bookingDates.to) {
      console.log('❌ To date missing');
      errors.to = 'Booking end date is required';
    }

    if (bookingDates.from && bookingDates.to) {
      const from = new Date(bookingDates.from);
      const to = new Date(bookingDates.to);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (isNaN(from.getTime())) {
        console.log('❌ From date is invalid');
        errors.from = 'Invalid start date';
      } else if (from < today) {
        console.log('❌ From date is in past');
        errors.from = 'Start date cannot be in the past';
      }

      if (isNaN(to.getTime())) {
        console.log('❌ To date is invalid');
        errors.to = 'Invalid end date';
      } else if (to < from) {
        console.log('❌ To date before from');
        errors.to = 'End date must be after start date';
      }
    }

    if (!formData.eventName?.trim()) {
      console.log('❌ Event name missing');
      errors.eventName = 'Event name is required';
    }

    if (!formData.applicantName?.trim()) {
      console.log('❌ Applicant name missing');
      errors.applicantName = 'Applicant name is required';
    }

    if (!formData.mobile?.trim()) {
      console.log('❌ Mobile missing');
      errors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.replace(/\D/g, ''))) {
      console.log('❌ Mobile format invalid');
      errors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.email?.trim()) {
      console.log('❌ Email missing');
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      console.log('❌ Email format invalid');
      errors.email = 'Please enter a valid email address';
    }

    console.log('Total errors:', Object.keys(errors).length);
    console.log('Errors object:', errors);
    console.log('=== END VALIDATION ===');

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Process payment with CCAvenue
   */
const processPayment = async (bookingData: any) => {
  try {
    console.log('=== PROCESS PAYMENT STARTED ===');
    console.log('Received bookingData:', bookingData);
    
    setIsProcessingPayment(true);

    // ✅ FIX: Get venue from bookingData instead of selectedVenue state
    const venue = bookingData.venue || selectedVenue;
    
    if (!venue) {
      throw new Error('Venue information is missing');
    }

    console.log('Using venue:', venue);

    // Extract costs from bookingData
    const costs = bookingData.costs;

    // ✅ Build payload using venue from bookingData
    const bookingPayload = {
      venueId: venue.id, // ✅ Use venue from bookingData
      eventName: bookingData.eventName,
      applicantName: bookingData.applicantName,
      mobile: bookingData.mobile,
      email: bookingData.email,
      gstNumber: bookingData.gstNumber || '',
      bookedFrom: bookingData.bookingDates.from,
      bookedTo: bookingData.bookingDates.to,
      prefixDays: bookingData.prefixDays || 0,
      suffixDays: bookingData.suffixDays || 0,
      rentAmount: costs.rent,
      securityDeposit: costs.securityDeposit,
      gst: costs.gst,
      totalAmount: costs.total,
      // ✅ Add timeSlots if present (for shooting venues)
      ...(bookingData.timeSlots && { timeSlots: bookingData.timeSlots })
    };

    console.log('📤 Booking payload to API:', bookingPayload);

    // Step 1: Create booking
    const bookingResponse = await axios.post(
      `${API_BASE_URL}/bookings/public`,
      bookingPayload
    );

    console.log('✅ Booking created:', bookingResponse.data);

    const booking = bookingResponse.data.data;

    // Step 2: Create payment order
    const paymentResponse = await axios.post(`${API_BASE_URL}/payments/public`, {
      bookingId: booking.id,
      amount: costs.total,
      paymentGateway: 'kotak_ccavenue'
    });

    console.log('✅ Payment order created:', paymentResponse.data);

    const { paymentUrl, orderId } = paymentResponse.data.data;

    // Step 3: Store for callback (also store venue info)
    sessionStorage.setItem('pendingBooking', JSON.stringify({
      bookingId: booking.id,
      bookingNumber: booking.bookingNumber,
      orderId: orderId,
      venue: venue.name, // ✅ Use venue from bookingData
      venueLocation: venue.location, // ✅ Store location too
      venueType: venue.type, // ✅ Store type for receipt
      eventName: bookingData.eventName,
      applicantName: bookingData.applicantName,
      mobile: bookingData.mobile,
      email: bookingData.email,
      gstNumber: bookingData.gstNumber || '',
      bookedFrom: bookingData.bookingDates.from,
      bookedTo: bookingData.bookingDates.to,
      prefixDays: bookingData.prefixDays || 0,
      suffixDays: bookingData.suffixDays || 0,
      timeSlots: bookingData.timeSlots, // ✅ Store time slots
      costs: costs
    }));

    console.log('🔄 Redirecting to CCAvenue:', paymentUrl);
    
    // Step 4: Redirect to payment gateway
    window.location.href = paymentUrl;

  } catch (error: any) {
    setIsProcessingPayment(false);
    console.error('❌ Payment error:', error);
    console.error('Error response:', error.response?.data);
    
    const errorMessage = error.response?.data?.message || error.message || 'Payment initialization failed';
    alert(`Error: ${errorMessage}\n\nPlease try again or contact support at 040-27018115`);
  }
};
  /**
   * Handle payment callback from CCAvenue
   * This should be called when user returns from CCAvenue payment page
   */
useEffect(() => {
  const handlePaymentCallback = async () => {
    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const encResponse = urlParams.get('encResp');
    const reason = urlParams.get('reason');
    
    if (paymentStatus) {
      console.log('📥 Payment callback received:', { paymentStatus, hasEncResp: !!encResponse });
      
      if (paymentStatus === 'success' && encResponse) {
        try {
          setIsLoading(true);
          
          // Get pending booking data
          const pendingBookingData = sessionStorage.getItem('pendingBooking');
          if (pendingBookingData) {
            const bookingData = JSON.parse(pendingBookingData);
            
            // Format receipt
            const formatDate = (dateStr: string) => {
              return new Date(dateStr).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              });
            };

            const receipt = {
              bookedBy: bookingData.applicantName,
              bookingNumber: bookingData.bookingNumber,
              venue: bookingData.venue,
              venueType: selectedVenue?.type || 'event',
              prefixDays: bookingData.prefixDays,
              bookedFrom: formatDate(bookingData.bookedFrom),
              bookedTo: formatDate(bookingData.bookedTo),
              suffixDays: bookingData.suffixDays,
              status: 'Confirmed',
              bookingAmount: bookingData.costs.total,
              paymentId: bookingData.orderId,
              email: bookingData.email,
              mobile: bookingData.mobile,
              eventName: bookingData.eventName,
              gstNumber: bookingData.gstNumber || 'N/A',
              rentAmount: bookingData.costs.rent,
              prefixSuffixRent: bookingData.costs.prefixSuffixRent,
              securityDeposit: bookingData.costs.securityDeposit,
              gst: bookingData.costs.gst,
              bookingDate: new Date().toLocaleString('en-IN', {
                dateStyle: 'medium',
                timeStyle: 'short'
              }),
              venueLocation: selectedVenue?.location || ''
            };

            setAllBookings(prev => [...prev, receipt]);
            setReceiptData(receipt);
            setShowReceiptModal(true);

            // Clear session storage
            sessionStorage.removeItem('pendingBooking');

            // Show success message
            setTimeout(() => {
              alert('🎉 Booking confirmed! Your receipt has been generated.');
            }, 500);
          }
        } catch (error: any) {
          console.error('Error processing payment success:', error);
          alert('Payment successful but failed to generate receipt. Please contact support.');
        } finally {
          setIsLoading(false);
        }
      } else if (paymentStatus === 'failed' || paymentStatus === 'error') {
        alert(`❌ Payment failed: ${reason || 'Please try again'}`);
        sessionStorage.removeItem('pendingBooking');
      }

      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  handlePaymentCallback();
}, []);

  const getTermsAndConditions = (venueType: string) => {
    if (venueType === 'event') {
      return [
        'The party will be conducted within the area and spot as allotted by the A.O, BPP Parks.',
        'The number of guests is restricted to 300 only.',
        'You are requested to keep the surroundings neat and clean without causing any damage to lawns.',
        'The security Agency will keep one security guard at the party till the celebration is over.',
        'The official of BPP, HMDA / Security Agency will reserve the right to enter the party zone to check / monitor during the function.',
        'The organizer and invitees should park their vehicles in the parking area.',
        'The organizer shall vacate the premises within the scheduled time.',
        'In case of any problem the organizer may contact A.O, BPP (Parks).',
        'This area is plastic freezone. No plastic shall be allowed into the park.',
        'The organizer shall not use any loud speakers nor music systems during the functions.',
        'Erection of Stage and formal function is not allowed.',
        'This permission shall not cause any hinder to the Government/ HMDA programmer at the park.',
        'The Security Deposit shall be refunded on receipt of report of the A.O, BPP (Parks).',
        'Entry into park will be permitted from 9:00 A.M on the day of event.'
      ];
    } else if (venueType === 'shooting') {
      return [
        'If the shooting session exceeds (1 hour) in succession, an extra amount for (4) hours will be charged.',
        'The necessary permissions from Police (Law and orders) and traffic shall be obtained before commencement.',
        'The agency should keep his security personnel to avoid the traffic problem.',
        'The permission accorded shall be withdrawn at any time without assigning any reasons.',
        'The agency should take appropriate steps to protect the environment.',
        'In case of cancellation of shooting by applicant the entire charges will be forfeited.',
        'Postponement is permitted twice only with a penalty of Rs.2000/- each time.',
        'The said activity should not cause any disturbance to the visitors and road users.',
        'The HMDA/BPP shall not take any responsibility for any injury / accident / death.',
        'The applicant should visit the area in advance and make a recce to prevent accidents.'
      ];
    }
    return [];
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2000);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
      @keyframes slideIn { from { transform: translateX(-100%); } to { transform: translateX(0); } }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      ::-webkit-scrollbar { width: 10px; }
      ::-webkit-scrollbar-track { background: ${theme.gray100}; }
      ::-webkit-scrollbar-thumb { background: ${theme.govBlue}; border-radius: 5px; }
    `;
    document.head.appendChild(style);
    return () => { if (style.parentNode) style.parentNode.removeChild(style); };
  }, []);

  const { 
    venues: apiVenues, 
    loading: venuesLoading, 
    error: venuesError,
    refetch: refetchVenues 
  } = useActiveVenues();

  const venues: UIVenue[] = transformVenuesForUI(apiVenues);
  const shootingVenues = venues.filter(v => v.type === 'shooting');
const eventVenues = venues.filter(v => v.type === 'event');

  if (isLoading || venuesLoading) return <Loader />;

  if (venuesError) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '24px',
        textAlign: 'center'
      }}>
        <AlertCircle size={64} style={{ color: theme.error, marginBottom: '16px' }} />
        <h2 style={{ fontSize: '2rem', fontWeight: '800', color: theme.error, marginBottom: '16px' }}>
          Failed to Load Venues
        </h2>
        <p style={{ color: theme.gray600, marginBottom: '24px' }}>
          {venuesError.message}
        </p>
        <button
          onClick={() => refetchVenues()}
          style={{
            background: theme.govBlue,
            color: theme.white,
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '1rem'
          }}
        >
          Retry Loading Venues
        </button>
      </div>
    );
  }

  const faqCategories = [
    {
      category: 'Booking & Payment',
      icon: <CreditCard size={24} />,
      color: theme.govBlue,
      questions: [
        {
          q: 'What are the rental charges for Peoples Plaza?',
          a: 'Peoples Plaza charges ₹1,50,000 (Rupees One Lakh Fifty Thousand only) per day. Additional prefix/suffix rent is ₹11,000 per day for erection and dismantling. Maximum 3 days each for prefix and suffix (total 6 days). Normal rent applies for days exceeding this limit.'
        },
        {
          q: 'What is the security deposit amount?',
          a: 'The security deposit is a fixed amount per venue, not a proportion of the rent — ₹25,000 for Peoples Plaza and Himayat Sagar Eco Hill Park (Party Zone), and ₹15,000 for Gandipet Park (Open Air Theatre). The exact amount for the venue you select is shown on its card and in the booking summary. It must be paid by draft, banker\'s cheque, or pay order in favour of Vice Chairman, BPPA. The deposit is refundable after event completion, subject to no damages and proper venue handover.'
        },
        {
          q: 'What payment methods are accepted?',
          a: 'Payments can be made via draft, banker\'s cheque, pay order in favour of Vice Chairman BPPA, or online through UPI, credit/debit cards, and net banking.'
        },
        {
          q: 'When should I pay for short-term bookings?',
          a: 'For short-term events (≤30 days): Applications with prescribed rent must be submitted 2 months before the event date. Full rent is required for blocking dates. Allotment is confirmed after receipt of full rent along with security deposit.'
        },
        {
          q: 'What is the payment schedule for long-term bookings?',
          a: 'For events longer than 30 days: Applications accepted 4 months before event. Pay 50% advance initially for date blocking. Remaining 50% plus security deposit must be paid 1 month before event. Maximum booking restricted to 30 days; variety of events encouraged.'
        }
      ]
    },
    {
      category: 'Cancellation & Changes',
      icon: <AlertCircle size={24} />,
      color: theme.error,
      questions: [
        {
          q: 'What is the cancellation policy?',
          a: 'Applicant cancellation: 50% of total rent paid is forfeited, 50% refunded without interest. Government/BPPA/Police cancellation for unavoidable circumstances: 100% refund without interest. No exemption if event is closed partially during daytime. Once application accepted with prescribed rentals, it is binding on the applicant for blocking the date.'
        },
        {
          q: 'Can I postpone my booking?',
          a: 'Yes, under special circumstances with valid reasons, postponement is permissible ONLY ONCE. Additional charge: 25% of total rent. Subject to date availability and at sole discretion of BPPA. Same conditions apply as per Sl.No.4(a) & 4(b) regarding payment schedules.'
        },
        {
          q: 'Can I prepone my event date?',
          a: 'Preponing follows the same rules as postponement - allowed once with 25% additional charge, subject to availability and approval with valid justification.'
        },
        {
          q: 'What if I don\'t show up on booking date?',
          a: 'No-show bookings result in full forfeiture of all payments including security deposit. No refunds or rescheduling allowed for no-shows.'
        }
      ]
    },
    {
      category: 'Booking Requirements',
      icon: <FileText size={24} />,
      color: theme.secondaryBlue,
      questions: [
        {
          q: 'What documents are required for booking?',
          a: 'Required: ID proof (Aadhar/PAN/Passport/Driving License - upload required), GST number (if applicable), event name, applicant name, mobile number, alternate mobile, email ID, booking dates (From & To), prefix days, suffix days. You must physically check the venue before proceeding with booking.'
        },
        {
          q: 'How far in advance can I book?',
          a: 'Bookings can be made up to 180 days (6 months) in advance. Minimum advance booking: 60 days for short events, 120 days for long events (>30 days).'
        },
        {
          q: 'What is the maximum booking duration?',
          a: 'Maximum booking duration is 30 days. Longer bookings are considered on case-by-case basis with special approval from BPPA. Variety of events shall be encouraged instead of monotonous activities.'
        },
        {
          q: 'Do I need to visit physically before booking?',
          a: 'Yes, it is recommended to physically check the venue before proceeding with booking to verify suitability for your event requirements.'
        }
      ]
    },
    {
      category: 'Event Rules & Restrictions',
      icon: <Shield size={24} />,
      color: theme.warning,
      questions: [
        {
          q: 'Are religious or political activities allowed?',
          a: 'No. Religious and political activities, or any activities objectionable to government or civil society are strictly prohibited at all HMDA venues. The organizer should not conduct any religious/political activity.'
        },
        {
          q: 'Is alcohol allowed at venues?',
          a: 'No. Sale and consumption of liquor is strictly prohibited at all HMDA-managed venues without exception.'
        },
        {
          q: 'Can I transfer my booking to someone else?',
          a: 'No. Bookings are non-transferable. The allottee/applicant is not entitled to transfer the allotment to others. Events must be conducted in the name/banners shown in the allotment order. Transfer attempts result in booking cancellation.'
        },
        {
          q: 'What are night shooting rates?',
          a: 'Night shooting rates are DOUBLE the regular daytime rates. Night time is typically defined as after regular working hours (post 6 PM).'
        }
      ]
    },
    {
      category: 'Responsibilities & Compliance',
      icon: <CheckCircle size={24} />,
      color: theme.success,
      questions: [
        {
          q: 'What permissions do I need before the event?',
          a: 'Mandatory permissions required from: Local Police Department, Fire Authority, and other relevant state/central government bodies. Police-fixed timings must be strictly followed. The organizer shall take all mandatory permissions from concerned State/Central Government and other Local bodies.'
        },
        {
          q: 'Who is responsible for waste management?',
          a: 'Event organizer is fully responsible. The ground is located abutting to Hussain Sagar Lake, therefore must ensure no waste material, plastic bags, cups are thrown into the lake. Must provide dustbins, arrange garbage lifting, provide mobile toilets, and ensure no waste enters lake. Violations attract penalties up to Rs. 10,000.'
        },
        {
          q: 'What medical facilities are required?',
          a: 'Organizer must provide minimum medical facilities including first aid at the venue to handle any medical emergencies during the event.'
        },
        {
          q: 'Who handles event security?',
          a: 'Organizer must engage private security personnel at own cost for traffic control and maintaining peace near and inside the venue.'
        },
        {
          q: 'What about parking arrangements?',
          a: 'No vehicles/two-wheelers should be parked on roads abutting the venue or inside the ground. All vehicles must be diverted to designated parking lots with parking fees paid to BPPA/HMDA.'
        },
        {
          q: 'Do I need to arrange power supply?',
          a: 'Yes, organizer must make own arrangements for required power supply and pay charges directly to the concerned electricity agency.'
        }
      ]
    },
    {
      category: 'Penalties & Violations',
      icon: <AlertCircle size={24} />,
      color: theme.error,
      questions: [
        {
          q: 'What is the penalty for not cleaning the venue?',
          a: 'Rs. 5,000 will be forfeited from security deposit if the ground is not cleaned properly after the event.'
        },
        {
          q: 'What happens if I don\'t vacate on time?',
          a: 'Full security deposit will be forfeited if premises are not cleared/handed over within stipulated time. For extra/excess time utilization of ground, full amount of that day must be paid, otherwise your materials will be seized.'
        },
        {
          q: 'What are penalties for environmental violations?',
          a: 'Violations including waste disposal, lake pollution, or missing dustbins attract penalties which shall not be less than Rs. 10,000 in addition to damage cost recovery from security deposit.'
        },
        {
          q: 'What is blacklisting and when does it happen?',
          a: 'If any violations are noticed, not only penalties but the applicant/allottee will be blacklisted and will not be entitled for allotment of the site in future. Serious violations result in permanent barring from future bookings at all HMDA venues.'
        }
      ]
    },
    {
      category: 'Safety & Liability',
      icon: <Shield size={24} />,
      color: theme.accentPurple,
      questions: [
        {
          q: 'Who is liable for accidents during events?',
          a: 'In case of any accident/injury/death to any person or loss of property of any individual, the organizer shall be solely held responsible and should pay compensation as required under existing laws. The BPPA/HMDA shall not be dragged into any legal dispute concerning the matter.'
        },
        {
          q: 'What safety standards must be followed?',
          a: 'All safety standards shall be scrupulously followed in case of any equipment, amusement rides and miscellaneous items. BPPA/HMDA shall not be held responsible in any manner with regards to maintenance of safety standards.'
        },
        {
          q: 'What precautions are mandatory?',
          a: 'All mandatory precautions and preventive measures should be taken to protect the property and life of citizens/visitors. This is organizer\'s sole responsibility.'
        }
      ]
    },
    {
      category: 'Additional Information',
      icon: <Info size={24} />,
      color: theme.govBlue,
      questions: [
        {
          q: 'What defines "one day" in booking terms?',
          a: 'One day means 24 hours from 12:00 AM (Midnight) to 12:00 AM (Midnight) the next day.'
        },
        {
          q: 'What about GST charges?',
          a: 'GST is charged at 18% of the total Rental Amount excluding Security Deposit (refundable). The GST amount is calculated on the base rent only.'
        },
        {
          q: 'What are prefix and suffix days options?',
          a: 'Prefix days options: For 1 to 2 days - no Prefix and Suffix days; For 3 to 7 days - 1 Prefix day and 1 Suffix day; For 8 to 15 days - 2 Prefix days and 2 Suffix days; For Above 15 days - 3 Prefix days and 3 Suffix days. These are for erection and dismantling, charged at the venue\'s published per-day prefix/suffix rate.'
        },
        {
          q: 'Are there reserved seats for officials?',
          a: 'Yes, when seating is arranged, 3 seats/chairs must be reserved amongst audience in favour of personnel/Officers of BPPA/HMDA in order to facilitate monitoring.'
        },
        {
          q: 'Can officials access the venue during events?',
          a: 'Yes, the Officers/Security Personnel of BPPA/HMDA shall have access to the venue including the stage at all times and even during the actual performance.'
        },
        {
          q: 'Can BPPA modify conditions after booking?',
          a: 'Yes, BPPA/HMDA reserves the right to add more conditions or modify the conditions at any time till the date of release of Security Deposit by BPPA/HMDA. The organizer is bound to comply with new/modified conditions.'
        },
        {
          q: 'Can BPPA cancel confirmed bookings?',
          a: 'Yes, if Government requires the venue for important state functions/programmes, the permission given will be cancelled by BPPA/HMDA. No responsibility will be taken by BPPA/HMDA for any loss caused to organizer. BPPA/HMDA reserves the right to cancel the permission given at any time without assigning any reason.'
        },
        {
          q: 'Who pays applicable taxes?',
          a: 'The tax as applicable shall be payable by the event manager/allottee to the respective Departments and BPPA will not be responsible.'
        },
        {
          q: 'Are there exemptions or reductions in rent?',
          a: 'No exemption or reduction in rent will be considered under any circumstances. However, 50% concession can be extended to Government Departments only. No concession for Semi-Government agencies/Corporations. Exemption considered only if event closed at instance of BPPA/Govt/Police for unavoidable circumstances.'
        },
        {
          q: 'Can I extend my event?',
          a: 'If any allottee/applicant intends to extend the event, the same will be considered subject to availability and at the sole discretion of BPPA, well in advance and on payment of rent for the extended period.'
        },
        {
          q: 'How are damages recovered?',
          a: 'Any damages caused by applicant/allottee, the cost of damages will be recovered from security deposit and balance (if any) refunded. However, if cost of damage is more than security deposit, the balance must be paid immediately.'
        },
        {
          q: 'What happens if rent is increased?',
          a: 'Whenever rent is increased, it will be applicable only to prospective applicants and will not be applicable where dates are already blocked/allotted.'
        },
        {
          q: 'Must the organizer clean the area?',
          a: 'Yes, the organizer shall get the area cleaned and leveled at own expense and make it usable for conduct of the programme.'
        },
        {
          q: 'Can the organizer fill up the area?',
          a: 'No, the organizer shall not fill up the area with any material or any act which pollute the area and the lake as per the Hon\'ble Supreme Court directions.'
        }
      ]
    }
  ];

  const filteredVenues = selectedType === 'all' ? venues : venues.filter(v => v.type === selectedType);

  if (isLoading) return <Loader />;

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
   
{/* Navigation */}
<nav style={{
  background: theme.white,
  boxShadow: isScrolled ? `0 4px 20px ${theme.gray200}` : 'none',
  position: 'sticky',
  top: 0,
  zIndex: 999,
  transition: 'all 0.3s ease',
    minHeight: isMobile ? '80px' : '100px' // ✅ INCREASED NAV HEIGHT

}}>
  <div style={{
    maxWidth: '1400px',
    margin: '0 auto',
    padding: isMobile ? '16px 16px' : '20px 24px', // ✅ INCREASED PADDING
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px'
  }}>
    {/* HMDA Logo */}
    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
      <img 
        src={`${process.env.PUBLIC_URL}/images/hmdalogo.png`}
        alt="HMDA Logo"
        style={{
          height: isMobile ? '60px' : '75px',
          width: 'auto',
          objectFit: 'contain'
        }}
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />

        <img 
        src={`${process.env.PUBLIC_URL}/images/Telangana_Rising-cure-pure-rare.png`}
        alt="HMDA Rising"
       style={{
            height: isMobile ? '75px' : '90px',        // ✅ MUCH BIGGER - from 65px/80px to 75px/90px
            width: 'auto',
            maxWidth: isMobile ? '220px' : '280px',    // ✅ INCREASED max width
            objectFit: 'contain'
          }}
        onError={(e) => {
          e.currentTarget.style.display = 'none';
        }}
      />

    </div>



    {/* Navigation Buttons - Always Visible */}
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '6px' : '8px',
      alignItems: 'stretch',
      flex: isMobile ? 1 : 'initial'
    }}>
      {[
        { 
          label: 'Film Shooting', 
          shortLabel: 'Film',
          href: 'https://filmin.telangana.gov.in/', 
          icon: <Building2 size={16} />, 
          highlight: true,
          external: true
        },
        { 
          label: 'PHOTO SHOOT',
          shortLabel: 'Photo',
          href: '#availability', 
          icon: <Calendar size={16} />, 
          highlight: true, 
          onClick: () => setShowAvailabilityModal(true) 
        },            
        { 
          label: 'Track',
          shortLabel: 'Track',
          href: '#track', 
          icon: <Search size={16} />, 
          onClick: () => setShowTrackModal(true) 
        }
      ].map((item, idx) => (
        <a
          key={idx}
          href={item.href}
          onClick={(e) => {
            if (item.onClick) {
              e.preventDefault();
              item.onClick();
            }
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            color: item.highlight ? theme.white : theme.gray900,
            background: item.highlight 
              ? `linear-gradient(135deg, ${theme.govBlue}, ${theme.accentPurple})` 
              : 'transparent',
            textDecoration: 'none',
            fontWeight: '600',
            padding: isMobile ? '8px 10px' : '10px 20px',
            borderRadius: '10px',
            transition: 'all 0.3s ease',
            fontSize: isMobile ? '0.75rem' : '0.95rem',
            whiteSpace: 'nowrap',
            border: !item.highlight && !isMobile ? `2px solid ${theme.gray200}` : 'none'
          }}
          onMouseEnter={(e) => {
            if (!item.highlight) {
              e.currentTarget.style.background = theme.gray100;
            }
          }}
          onMouseLeave={(e) => {
            if (!item.highlight) {
              e.currentTarget.style.background = 'transparent';
            }
          }}
        >
          {item.icon}
          {isMobile ? item.shortLabel : item.label}
        </a>
      ))}
    </div>
  </div>
</nav>


{/* Featured Venues Section */}
<section style={{ padding: '40px 24px', background: theme.white }} id="venues">
  <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
  

  <div style={{
  display: 'grid',
  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',  // ✅ 3 columns
  gap: '24px'
}}>
  {eventVenues.map((venue, idx) => (
        <div
          key={idx}
          style={{
            background: theme.white,
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            border: `2px solid ${theme.gray100}`,
            transition: 'all 0.3s ease',
            animation: 'fadeIn 0.6s ease forwards',
            animationDelay: `${idx * 0.1}s`,
            opacity: 0
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
          }}
        >
          {/* IMAGE SECTION - FIXED */}
          <div style={{
            height: '200px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <img
              src={venue.image}
              alt={venue.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            />
            
            {/* Gradient Overlay */}
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.3) 100%)',
              pointerEvents: 'none'
            }} />

            {/* Availability Badge */}
            <div style={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              background: venue.availability === 'Available' ? theme.success : theme.error,
              color: theme.white,
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}>
              <div style={{
                width: '6px',
                height: '6px',
                background: theme.white,
                borderRadius: '50%',
                animation: 'pulse 2s infinite'
              }} />
              {venue.availability}
            </div>

            {/* Rating Badge */}
            <div style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)',
              padding: '6px 12px',
              borderRadius: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
            }}>
              <Star size={14} fill={theme.warning} style={{ color: theme.warning }} />
              <span style={{ fontWeight: '700', fontSize: '12px', color: theme.gray900 }}>
              </span>
            </div>
          </div>

          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '12px' }}>
              <h3 style={{ fontSize: '1.2rem', fontWeight: '800', marginBottom: '8px', color: theme.gray900 }}>
                {venue.name}
              </h3>
              <p style={{ 
                color: theme.gray600, 
                fontSize: '0.85rem', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                fontWeight: '500'
              }}>
                <MapPin size={14} /> {venue.location}
              </p>
            </div>

           <p style={{ 
  color: theme.gray700, 
  marginBottom: '16px', 
  lineHeight: 1.6,
  fontSize: '0.85rem'
}}>
  {venue.description || 'No description available'}
</p>

        <div style={{
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '12px',
  marginBottom: '16px',
  padding: '12px',
  background: `${venue.color}08`,
  borderRadius: '12px',
  border: `2px solid ${venue.color}20`
}}>
  <div>
    <p style={{ fontSize: '10px', color: theme.gray600, marginBottom: '4px', fontWeight: '600', letterSpacing: '0.5px' }}>
      RATE
    </p>
    <p style={{ fontSize: '16px', fontWeight: '800', color: theme.gray900 }}>
      ₹{venue.rate.toLocaleString()}
    </p>
    <p style={{ fontSize: '10px', color: theme.gray600 }}>{venue.rateType}</p>
  </div>
  
  {/* Security Deposit Info — from `venue.metadata`: a percentage of the rent
      where published, else the flat amount; NIL where neither is published.
      There are no booking dates on the card, so a percent venue shows the rate
      rather than a rupee figure it cannot compute yet. */}
  <div>
    <p style={{ fontSize: '10px', color: theme.gray600, marginBottom: '4px', fontWeight: '600', letterSpacing: '0.5px' }}>
      SECURITY DEPOSIT
    </p>
    <p style={{ fontSize: '16px', fontWeight: '800', color: theme.gray900 }}>
      {getSecurityDepositPercent(venue) != null
        ? `${getSecurityDepositPercent(venue)}%`
        : getSecurityDeposit(venue, 0) > 0
          ? `₹${getSecurityDeposit(venue, 0).toLocaleString()}`
          : 'NIL'}
    </p>
    <p style={{ fontSize: '10px', color: theme.gray600 }}>
      {getSecurityDepositPercent(venue) != null
        ? 'of venue rent'
        : getSecurityDeposit(venue, 0) > 0 ? 'fixed deposit' : 'no deposit'}
    </p>
  </div>

  {/* GST Info */}
  <div>
    <p style={{ fontSize: '10px', color: theme.gray600, marginBottom: '4px', fontWeight: '600', letterSpacing: '0.5px' }}>
      GST
    </p>
    <p style={{ fontSize: '16px', fontWeight: '800', color: theme.gray900 }}>
      {getGstRate(venue) * 100}%
    </p>
    <p style={{ fontSize: '10px', color: theme.gray600 }}>on rent</p>
  </div>

  <div>
    <p style={{ fontSize: '10px', color: theme.gray600, marginBottom: '4px', fontWeight: '600', letterSpacing: '0.5px' }}>
      BOOKINGS
    </p>
    <p style={{ fontSize: '16px', fontWeight: '800', color: theme.gray900 }}>
      {venue.bookings}+
    </p>
    <p style={{ fontSize: '10px', color: theme.gray600 }}>this month</p>
  </div>
</div>

{/* Featured Venues Section - Book Now Button */}
<button 
  style={{
    width: '100%',
    background: `linear-gradient(135deg, ${venue.color}, ${venue.color}dd)`,
    color: theme.white,
    border: 'none',
    padding: '14px',
    borderRadius: '12px',
    fontSize: '0.95rem',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.3s ease',
    boxShadow: `0 4px 12px ${venue.color}40`
  }}
  onClick={() => {
    if (venue.isMovieShooting) {
      window.location.href = '/movie-shooting';
    } else {
      // ✅ Clear dates when booking directly from venue card
      setSelectedVenue(venue);
      setBookingDates({ from: '', to: '' }); // Empty dates for fresh booking
      // Show the restriction notice first for Peoples Plaza, then continue to booking
      if (venue.name === 'Peoples Plaza') {
        setShowPlazaNotice(true);
      } else {
        setShowBookingModal(true);
      }
    }
  }}
  onMouseEnter={(e) => {
    e.currentTarget.style.transform = 'translateY(-2px)';
    e.currentTarget.style.boxShadow = `0 6px 16px ${venue.color}60`;
  }}
  onMouseLeave={(e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = `0 4px 12px ${venue.color}40`;
  }}
>
  <Calendar size={18} />
  {venue.isMovieShooting ? 'View Locations' : 'Book Now'}
</button>
          </div>
        </div>
      ))}
    </div>
  </div>
</section>

      {/* Process Section */}
      <section style={{ padding: '100px 24px', background: `linear-gradient(180deg, ${theme.white} 0%, ${theme.cream} 100%)` }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '80px' }}>
            <h2 style={{
              fontSize: isMobile ? '2.5rem' : '3.8rem',
              fontWeight: '900',
              marginBottom: '20px',
              background: `linear-gradient(135deg, ${theme.govBlue}, ${theme.secondaryBlue})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              How It Works
            </h2>
            <p style={{ fontSize: '1.2rem', color: theme.gray600, maxWidth: '600px', margin: '0 auto' }}>
              Book your venue in 4 simple steps
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: '32px',
            position: 'relative'
          }}>
            {!isMobile && (
              <div style={{
                position: 'absolute',
                top: '80px',
                left: '12.5%',
                right: '12.5%',
                height: '4px',
                background: `linear-gradient(90deg, ${theme.govBlue}, ${theme.secondaryBlue})`,
                opacity: 0.2,
                zIndex: 0
              }} />
            )}

            {[
              { icon: <Search size={32} />, title: 'Search Venue', desc: 'Browse and select from 12+ premium locations', color: theme.govBlue },
              { icon: <Calendar size={32} />, title: 'Check Dates', desc: 'Verify availability for your preferred dates', color: theme.secondaryBlue },
              { icon: <FileText size={32} />, title: 'Fill Details', desc: 'Complete booking form with event information', color: theme.accentPurple },
              { icon: <CreditCard size={32} />, title: 'Make Payment', desc: 'Secure online payment with instant confirmation', color: theme.govBlue }
            ].map((step, idx) => (
              <div
                key={idx}
                style={{
                  background: theme.white,
                  padding: '40px 24px',
                  borderRadius: '20px',
                  textAlign: 'center',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                  position: 'relative',
                  zIndex: 1,
                  border: `2px solid ${theme.gray100}`,
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  background: `linear-gradient(135deg, ${step.color}, ${step.color}dd)`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.white,
                  margin: '0 auto 24px',
                  boxShadow: `0 10px 25px ${step.color}40`,
                  position: 'relative'
                }}>
                  {step.icon}
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '-10px',
                    width: '32px',
                    height: '32px',
                    background: theme.white,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '800',
                    fontSize: '14px',
                    color: step.color,
                    border: `3px solid ${step.color}`
                  }}>
                    {idx + 1}
                  </div>
                </div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: '800', marginBottom: '12px', color: theme.gray900 }}>
                  {step.title}
                </h3>
                <p style={{ color: theme.gray600, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <section style={{ padding: '100px 24px', background: theme.white }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '64px' }}>
            <div style={{
              display: 'inline-block',
              background: `${theme.govBlue}10`,
              padding: '8px 20px',
              borderRadius: '30px',
              marginBottom: '20px',
              border: `2px solid ${theme.govBlue}`
            }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: theme.govBlue }}>
                TERMS & CONDITIONS
              </span>
            </div>

            <h2 style={{
              fontSize: isMobile ? '2.5rem' : '3.8rem',
              fontWeight: '900',
              marginBottom: '20px',
              background: `linear-gradient(135deg, ${theme.govBlue}, ${theme.secondaryBlue})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Frequently Asked Questions
            </h2>

            <p style={{ fontSize: '1.2rem', color: theme.gray600, maxWidth: '700px', margin: '0 auto' }}>
              Everything you need to know about booking HMDA venues
            </p>
          </div>

          <div style={{ display: 'grid', gap: '24px' }}>
            {faqCategories.map((category, catIdx) => (
              <div
                key={catIdx}
                style={{
                  background: theme.white,
                  borderRadius: '20px',
                  border: `2px solid ${theme.gray100}`,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}
              >
                <div style={{
                  background: `${category.color}10`,
                  padding: '24px',
                  borderBottom: `2px solid ${theme.gray100}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: category.color,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.white
                  }}>
                    {category.icon}
                  </div>
                  <h3 style={{
                    fontSize: '1.5rem',
                    fontWeight: '800',
                    color: theme.gray900
                  }}>
                    {category.category}
                  </h3>
                </div>

                <div style={{ padding: '8px' }}>
                  {category.questions.map((faq, qIdx) => {
                    const index = `${catIdx}-${qIdx}`;
                    const isOpen = openFaq === index;

                    return (
                      <div
                        key={qIdx}
                        style={{
                          borderBottom: qIdx < category.questions.length - 1 ? `1px solid ${theme.gray100}` : 'none'
                        }}
                      >
                        <button
                          onClick={() => setOpenFaq(isOpen ? null : index)}
                          style={{
                            width: '100%',
                            padding: '20px 24px',
                            background: 'none',
                            border: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '16px',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = theme.gray50}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          <span style={{
                            fontSize: '1.05rem',
                            fontWeight: '700',
                            color: theme.gray900,
                            flex: 1
                          }}>
                            {faq.q}
                          </span>
                          <ChevronRight
                            size={24}
                            style={{
                              color: category.color,
                              transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s ease',
                              flexShrink: 0
                            }}
                          />
                        </button>

                        <div style={{
                          maxHeight: isOpen ? '1000px' : '0',
                          overflow: 'hidden',
                          transition: 'max-height 0.3s ease'
                        }}>
                          <div style={{
                            padding: '0 24px 24px 24px',
                            color: theme.gray700,
                            lineHeight: 1.8,
                            fontSize: '0.95rem'
                          }}>
                            {faq.a}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '64px',
            padding: '40px',
            background: `linear-gradient(135deg, ${theme.govBlue}10, ${theme.secondaryBlue}10)`,
            borderRadius: '20px',
            border: `2px solid ${theme.govBlue}`,
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '1.8rem', fontWeight: '800', marginBottom: '16px', color: theme.gray900 }}>
              Still Have Questions?
            </h3>
            <p style={{ fontSize: '1.1rem', color: theme.gray600, marginBottom: '24px' }}>
              Our support team is here to help you
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="tel:04027018115" style={{
                background: `linear-gradient(135deg, ${theme.govBlue}, ${theme.accentPurple})`,
                color: theme.white,
                padding: '14px 28px',
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'transform 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                <Phone size={20} />
                Call: 040-27018115
              </a>
              <a href="mailto:support@hmda.gov.in" style={{
                background: theme.white,
                color: theme.govBlue,
                border: `2px solid ${theme.govBlue}`,
                padding: '14px 28px',
                borderRadius: '12px',
                textDecoration: 'none',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'transform 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                <Mail size={20} />
                Email Support
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: theme.gray900, color: theme.gray100, padding: isMobile ? '60px 24px' : '80px 24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: '48px',
            marginBottom: '48px'
          }}>
            <div>
              <h3 style={{ color: theme.white, fontSize: '1.5rem', fontWeight: '800', marginBottom: '16px' }}>
                HMDA Booking
              </h3>
              <p style={{ marginBottom: '20px', lineHeight: 1.7, fontSize: '0.95rem' }}>
                Official Hyderabad Metropolitan Development Authority venue booking platform
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                {[Facebook, Twitter, Instagram, Youtube].map((Icon, idx) => (
                  <a key={idx} href="#" style={{
                    width: '42px',
                    height: '42px',
                    background: theme.gray800,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.gray100,
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = theme.govBlue;
                    e.currentTarget.style.color = theme.white;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = theme.gray800;
                    e.currentTarget.style.color = theme.gray100;
                  }}>
                    <Icon size={20} />
                  </a>
                ))}
              </div>
            </div>

            {[
              { title: 'Quick Links', links: ['Book Venue', 'Check Availability', 'My Bookings', 'Guidelines', 'FAQs'] },
              { title: 'Venues', links: ['Peoples Plaza', 'Necklace Road', 'Sanjeevaiah Park', 'Lumbini Park', 'NTR Gardens'] },
              { title: 'Support', links: ['Contact Us', 'Help Center', 'Terms', 'Privacy', 'Sitemap'] }
            ].map((section, idx) => (
              <div key={idx}>
                <h4 style={{ color: theme.white, fontSize: '1.2rem', fontWeight: '700', marginBottom: '20px' }}>
                  {section.title}
                </h4>
                <ul style={{ listStyle: 'none' }}>
                  {section.links.map((link, i) => (
                    <li key={i} style={{ marginBottom: '12px' }}>
                      <a href="#" style={{
                        color: theme.gray100,
                        textDecoration: 'none',
                        fontSize: '0.95rem',
                        transition: 'color 0.3s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = theme.govBlue}
                      onMouseLeave={(e) => e.currentTarget.style.color = theme.gray100}>
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div style={{
            borderTop: `1px solid ${theme.gray800}`,
            paddingTop: '32px',
            textAlign: 'center'
          }}>
            <p style={{ fontSize: '14px' }}>
              © 2025 Hyderabad Metropolitan Development Authority. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

{/* ⚠️ Peoples Plaza Booking Restriction Notice - Closable Modal */}
{showPlazaNotice && (
  <div
    onClick={() => setShowPlazaNotice(false)}
    style={{
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '16px'
    }}
    role="dialog"
    aria-modal="true"
    aria-labelledby="plaza-notice-title"
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: theme.white,
        borderRadius: '18px',
        maxWidth: '560px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
        position: 'relative'
      }}
    >
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #D97706, #B45309)',
        padding: isMobile ? '18px 20px' : '22px 28px',
        borderTopLeftRadius: '18px',
        borderTopRightRadius: '18px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <AlertCircle size={isMobile ? 24 : 28} style={{ color: theme.white, flexShrink: 0 }} />
        <h2 id="plaza-notice-title" style={{
          margin: 0,
          fontSize: isMobile ? '1.05rem' : '1.25rem',
          fontWeight: 800,
          color: theme.white,
          lineHeight: 1.3
        }}>
          ⚠️ Important Notice – People's Plaza Booking
        </h2>
        {/* Close (X) */}
        <button
          onClick={() => setShowPlazaNotice(false)}
          aria-label="Close notice"
          style={{
            marginLeft: 'auto',
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: theme.white,
            flexShrink: 0
          }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: isMobile ? '20px' : '24px 28px' }}>
        <p style={{
          margin: '0 0 14px',
          fontSize: isMobile ? '0.95rem' : '1.05rem',
          fontWeight: 700,
          color: theme.gray900
        }}>
          Temporary Restriction on Event Booking Area
        </p>
        <p style={{ margin: '0 0 12px', fontSize: isMobile ? '0.85rem' : '0.95rem', lineHeight: 1.65, color: '#374151' }}>
          Due to ongoing civil works for the construction of an RCC retaining wall,
          strengthening of the existing platform, refurbishment of the access way, and
          allied infrastructure works, a strip of land measuring <strong>40 feet in width</strong> from
          the retaining wall, admeasuring approximately <strong>2,924 sq. m.</strong>, is
          temporarily unavailable for event bookings.
        </p>
        <p style={{ margin: '0 0 12px', fontSize: isMobile ? '0.85rem' : '0.95rem', lineHeight: 1.65, color: '#374151' }}>
          Event organizers are requested to plan their events only within the available
          designated area. The restricted portion shall remain unavailable until completion
          of the civil works.
        </p>
        <p style={{ margin: '0 0 20px', fontSize: isMobile ? '0.85rem' : '0.95rem', lineHeight: 1.65, color: '#374151', fontStyle: 'italic' }}>
          We regret the inconvenience and appreciate your cooperation.
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => setShowPlazaNotice(false)}
            style={{
              padding: '12px 20px',
              borderRadius: '10px',
              border: `2px solid ${theme.gray200}`,
              background: theme.white,
              color: theme.gray900,
              fontWeight: 600,
              fontSize: '0.95rem',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
          <button
            onClick={() => { setShowPlazaNotice(false); setShowBookingModal(true); }}
            style={{
              padding: '12px 24px',
              borderRadius: '10px',
              border: 'none',
              background: `linear-gradient(135deg, ${theme.govBlue}, ${theme.accentPurple})`,
              color: theme.white,
              fontWeight: 700,
              fontSize: '0.95rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            I Understand, Continue to Booking
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  </div>
)}

{/* Booking Modal - Works for both paths */}
<BookingModal
  show={showBookingModal}
  onClose={() => {
    setShowBookingModal(false);
    // Clear dates when closing modal
    setBookingDates({ from: '', to: '' });
  }}
  selectedVenue={selectedVenue}
  isMobile={isMobile}
  theme={theme}
  onProcessPayment={processPayment}
  isProcessingPayment={isProcessingPayment}
  // ✅ Pass dates from parent state - will be empty or pre-filled depending on navigation path
  initialFromDate={bookingDates.from || undefined}
  initialToDate={bookingDates.to || undefined}
/>

      {/* Track Booking Modal */}
      {showTrackModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '24px'
        }}
        onClick={() => setShowTrackModal(false)}>
          <div style={{
            background: theme.white,
            borderRadius: '24px',
            maxWidth: '600px',
            width: '100%',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}>
            <div style={{
              padding: '32px',
              borderBottom: `2px solid ${theme.gray100}`,
              background: `linear-gradient(135deg, ${theme.govBlue}15, ${theme.secondaryBlue}15)`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h2 style={{
                  fontSize: '2rem',
                  fontWeight: '800',
                  color: theme.gray900,
                  marginBottom: '8px'
                }}>
                  View My Bookings
                </h2>
                <p style={{ color: theme.gray600, fontSize: '0.95rem' }}>
                  Track your booking status
                </p>
              </div>
              <button
                onClick={() => setShowTrackModal(false)}
                style={{
                  background: theme.gray100,
                  border: 'none',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: theme.gray700
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '32px' }}>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                  Booking Number (optional)
                </label>
                <input
                  type="text"
                  placeholder="Enter Booking Number (optional)"
                  value={trackingData.bookingNumber}
                  onChange={(e) => setTrackingData({ ...trackingData, bookingNumber: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${theme.gray200}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>
                  Applicant Mobile *
                </label>
                <input
                  type="tel"
                  placeholder="Enter Mobile Number"
                  value={trackingData.mobile}
                  onChange={(e) => setTrackingData({ ...trackingData, mobile: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `2px solid ${theme.gray200}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem'
                  }}
                />
              </div>

              {trackError && (
                <div style={{
                  padding: '16px 20px',
                  background: `${theme.error}10`,
                  border: `2px solid ${theme.error}40`,
                  borderRadius: '12px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={20} style={{ color: theme.error, flexShrink: 0 }} />
                  <span style={{ color: theme.gray700, fontSize: '0.95rem', fontWeight: '600' }}>
                    {trackError}
                  </span>
                </div>
              )}

              {bookingResults && bookingResults.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                    <CheckCircle size={20} style={{ color: theme.success }} />
                    <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: theme.gray900 }}>
                      {bookingResults.length === 1
                        ? 'Booking Found!'
                        : `${bookingResults.length} Bookings Found`}
                    </h3>
                  </div>
                  <div style={{ display: 'grid', gap: '16px', maxHeight: '360px', overflowY: 'auto' }}>
                    {bookingResults.map((booking: any, idx: number) => (
                      <div key={booking.number || idx} style={{
                        padding: '20px',
                        background: `${theme.success}10`,
                        border: `2px solid ${theme.success}40`,
                        borderRadius: '12px'
                      }}>
                        <div style={{ display: 'grid', gap: '12px', fontSize: '0.95rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: theme.gray600 }}>Booking Number:</span>
                            <span style={{ fontWeight: '700' }}>{booking.number}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: theme.gray600 }}>Venue:</span>
                            <span style={{ fontWeight: '700' }}>{booking.venue}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: theme.gray600 }}>Event Date:</span>
                            <span style={{ fontWeight: '700' }}>{booking.date}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: theme.gray600 }}>Status:</span>
                            <span style={{
                              fontWeight: '700',
                              color: theme.success,
                              background: `${theme.success}20`,
                              padding: '4px 12px',
                              borderRadius: '6px',
                              textTransform: 'capitalize'
                            }}>
                              {booking.status}
                            </span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: theme.gray600 }}>Amount:</span>
                            <span style={{ fontWeight: '700', color: theme.success }}>
                              ₹{booking.amount.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <button
                          disabled={downloadingNumber === booking.number}
                          onClick={() => handleDownloadReceipt(booking.number)}
                          style={{
                            marginTop: '16px',
                            width: '100%',
                            background: theme.white,
                            color: theme.govBlue,
                            border: `2px solid ${theme.govBlue}`,
                            padding: '10px',
                            borderRadius: '10px',
                            fontSize: '0.95rem',
                            fontWeight: '700',
                            cursor: downloadingNumber === booking.number ? 'not-allowed' : 'pointer',
                            opacity: downloadingNumber === booking.number ? 0.6 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <Download size={18} />
                          {downloadingNumber === booking.number ? 'Downloading...' : 'Download Receipt'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  disabled={isTracking}
                  onClick={async () => {
                    const bookingNumber = trackingData.bookingNumber.trim();
                    const mobile = trackingData.mobile.trim();
                    if (!mobile) {
                      setTrackError('Please enter your registered mobile number.');
                      return;
                    }
                    setIsTracking(true);
                    setTrackError('');
                    setBookingResults(null);
                    try {
                      // Booking number is optional; mobile alone returns all bookings for that number.
                      const params: { mobile: string; bookingNumber?: string } = { mobile };
                      if (bookingNumber) params.bookingNumber = bookingNumber;
                      const response = await axios.get(`${API_BASE_URL}/bookings/track`, { params });
                      const payload = response.data?.data ?? response.data;
                      // Normalise: single-booking lookups return an object, mobile-only returns an array.
                      const list = Array.isArray(payload) ? payload : payload ? [payload] : [];
                      if (list.length === 0) {
                        setTrackError('No bookings found for the details provided.');
                        return;
                      }
                      setBookingResults(
                        list.map((d: any) => ({
                          number: d.bookingNumber,
                          venue: d.venue || 'N/A',
                          date: d.bookedFrom
                            ? new Date(d.bookedFrom).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              })
                            : 'N/A',
                          status: d.status,
                          amount: Number(d.totalAmount) || 0,
                        }))
                      );
                    } catch (error: any) {
                      setBookingResults(null);
                      setTrackError(
                        error?.response?.data?.message ||
                          'Booking not found or mobile number does not match.'
                      );
                    } finally {
                      setIsTracking(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    background: `linear-gradient(135deg, ${theme.govBlue}, ${theme.accentPurple})`,
                    color: theme.white,
                    border: 'none',
                    padding: '14px',
                    borderRadius: '12px',
                    fontSize: '1.05rem',
                    fontWeight: '700',
                    cursor: isTracking ? 'not-allowed' : 'pointer',
                    opacity: isTracking ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Search size={20} />
                  {isTracking ? 'Searching...' : 'Search'}
                </button>
                <button
                  onClick={() => {
                    setShowTrackModal(false);
                    setBookingResults(null);
                    setTrackError('');
                    setTrackingData({ bookingNumber: '', mobile: '' });
                  }}
                  style={{
                    background: theme.white,
                    color: theme.gray700,
                    border: `2px solid ${theme.gray200}`,
                    padding: '14px 24px',
                    borderRadius: '12px',
                    fontSize: '1.05rem',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>

              <div style={{
                marginTop: '24px',
                padding: '16px',
                background: `${theme.secondaryBlue}10`,
                borderRadius: '12px',
                border: `2px solid ${theme.secondaryBlue}40`,
                fontSize: '0.85rem',
                color: theme.gray700,
                lineHeight: 1.6
              }}>
                <div style={{ fontWeight: '700', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: theme.secondaryBlue }}>
                  <Info size={16} />
                  Additional Services
                </div>
                <ul style={{ marginLeft: '20px', marginTop: '8px' }}>
                  <li>Prepone / Postpone Booking (25% extra charge)</li>
                  <li>Cancel Booking (50% refund)</li>
                  <li>Download Receipt</li>
                  <li>View Payment History</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

  {/* Check Availability Modal - NEW COMPONENT */}
<AvailabilityModal
  show={showAvailabilityModal}
  onClose={() => {
    setShowAvailabilityModal(false);
    setBookingDates({ from: '', to: '' });
  }}
  venues={shootingVenues}
  isMobile={isMobile}
  theme={theme}
  onProceedToPayment={processPayment}
  isProcessingPayment={isProcessingPayment}
/>

{/* Booking Receipt Modal */}
{showReceiptModal && receiptData && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10001,
    padding: '24px',
    overflowY: 'auto'
  }}
  onClick={() => setShowReceiptModal(false)}>
    <div 
      id="receipt-content"
      style={{
        background: theme.white,
        borderRadius: '24px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        position: 'relative'
      }}
      onClick={(e) => e.stopPropagation()}>
      
      {/* Action Buttons */}
      <div style={{
        padding: '20px 32px',
        borderBottom: `2px solid ${theme.gray100}`,
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end',
        background: theme.gray50
      }}
      className="no-print">
      <button
  onClick={async () => {
    const element = document.getElementById('receipt-content');
    if (!element) return;

    // Hide buttons before capturing
    const buttons = element.querySelector('.no-print') as HTMLElement;
    if (buttons) buttons.style.display = 'none';

    // Store original styles
    const originalOverflow = element.style.overflow;
    const originalMaxHeight = element.style.maxHeight;
    const originalHeight = element.style.height;

    try {
      // Remove scroll restrictions temporarily
      element.style.overflow = 'visible';
      element.style.maxHeight = 'none';
      element.style.height = 'auto';

      // Wait for layout to settle
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture the full content
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        scrollY: -window.scrollY,
        scrollX: -window.scrollX,
        allowTaint: true
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = 297; // A4 height in mm
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;

      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      // Add additional pages if content is longer than one page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`HMDA_Booking_${receiptData.bookingNumber}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      // Restore original styles
      element.style.overflow = originalOverflow;
      element.style.maxHeight = originalMaxHeight;
      element.style.height = originalHeight;
      
      // Show buttons again
      if (buttons) buttons.style.display = 'flex';
    }
  }}
  style={{
    padding: '10px 20px',
    background: theme.govBlue,
    color: theme.white,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: '600',
    fontSize: '14px'
  }}
>
  <Download size={18} />
  Download as PDF
</button>
        <button
          onClick={() => setShowReceiptModal(false)}
          style={{
            padding: '10px 20px',
            background: theme.gray200,
            color: theme.gray700,
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          Close
        </button>
      </div>

      {/* Header with Logos */}
      <div style={{
        background: theme.white,
        borderBottom: `4px solid ${theme.govBlue}`,
        padding: '30px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {/* HMDA Logo */}
        <div style={{
          width: '80px',
          height: '80px',
          background: `linear-gradient(135deg, ${theme.govBlue}, ${theme.govBlue})`,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.white,
          fontWeight: '800',
          fontSize: '20px'
        }}>
          HMDA
        </div>

        {/* Center Title */}
        <div style={{ textAlign: 'center', flex: 1, padding: '0 20px' }}>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '800',
            color: theme.govBlue,
            margin: '0 0 8px 0',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            HYDERABAD METROPOLITAN DEVELOPMENT AUTHORITY
          </h1>
          <div style={{
            fontSize: '16px',
            color: theme.gray700,
            fontWeight: '600',
            marginBottom: '8px'
          }}>
            హైదరాబాద్ మహానగర అభివృద్ధి సంస్థ
          </div>
          <div style={{
            fontSize: '18px',
            fontWeight: '700',
            color: theme.govBlue
          }}>
            Online BPP Booking System (BPP HMDA)
          </div>
        </div>

        {/* Government Seal */}
        <div style={{
          width: '80px',
          height: '80px',
          background: theme.gray100,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `3px solid ${theme.govBlue}`
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            background: theme.govBlue,
            borderRadius: '50%'
          }} />
        </div>
      </div>

      {/* Booking Details */}
      <div style={{ padding: '40px' }}>
        {/* Success Badge */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: `${theme.success}15`,
          color: theme.success,
          padding: '8px 16px',
          borderRadius: '8px',
          marginBottom: '24px',
          border: `2px solid ${theme.success}40`,
          fontWeight: '700'
        }}>
          <CheckCircle size={20} />
          Booking Confirmed - Payment Successful
        </div>

        {/* Booking Information Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '16px',
          marginBottom: '32px',
          padding: '24px',
          background: theme.gray100,
          borderRadius: '12px'
        }}>
          <div>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Booked By
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.gray900 }}>
              {receiptData.bookedBy}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Booking Number
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.govBlue }}>
              {receiptData.bookingNumber}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Venue
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.gray900 }}>
              {receiptData.venue}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Event Name
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.gray900 }}>
              {receiptData.eventName}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Prefix Days
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.gray900 }}>
              {receiptData.prefixDays}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Suffix Days
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.gray900 }}>
              {receiptData.suffixDays}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Booked From
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.gray900 }}>
              {receiptData.bookedFrom}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Booked To
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.gray900 }}>
              {receiptData.bookedTo}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Status
            </div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: theme.success }}>
              {receiptData.status}
            </div>
          </div>

          <div>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Payment ID
            </div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: theme.secondaryBlue }}>
              {receiptData.paymentId}
            </div>
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ fontSize: '12px', color: theme.gray600, fontWeight: '600', marginBottom: '4px' }}>
              Booking Amount (Total Paid)
            </div>
            <div style={{ fontSize: '24px', fontWeight: '900', color: theme.govBlue }}>
              ₹{receiptData.bookingAmount.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Payment Breakdown */}
        <div style={{
          padding: '24px',
          background: `${theme.secondaryBlue}08`,
          borderRadius: '12px',
          marginBottom: '32px',
          border: `2px solid ${theme.secondaryBlue}20`
        }}>
          <h3 style={{ fontSize: '1.3rem', fontWeight: '800', marginBottom: '16px', color: theme.gray900 }}>
            Payment Breakdown
          </h3>
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: `1px solid ${theme.gray200}` }}>
              <span style={{ color: theme.gray700 }}>Rent Amount:</span>
              <span style={{ fontWeight: '700' }}>₹{receiptData.rentAmount.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: `1px solid ${theme.gray200}` }}>
              <span style={{ color: theme.gray700 }}>Prefix/Suffix Rent:</span>
              <span style={{ fontWeight: '700' }}>₹{receiptData.prefixSuffixRent.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: `1px solid ${theme.gray200}` }}>
              <span style={{ color: theme.gray700 }}>Security Deposit (Refundable):</span>
              <span style={{ fontWeight: '700' }}>₹{receiptData.securityDeposit.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '8px', borderBottom: `2px solid ${theme.gray900}` }}>
              <span style={{ color: theme.gray700 }}>GST (18%):</span>
              <span style={{ fontWeight: '700' }}>₹{receiptData.gst.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1.2rem' }}>
              <span style={{ fontWeight: '800', color: theme.gray900 }}>Total Amount:</span>
              <span style={{ fontWeight: '900', color: theme.govBlue }}>₹{receiptData.bookingAmount.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Electronic Signature Note - UPDATED */}
        <div style={{
          padding: '24px',
          background: `${theme.govBlue}08`,
          borderRadius: '12px',
          marginBottom: '32px',
          border: `2px solid ${theme.govBlue}30`,
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <CheckCircle size={20} style={{ color: theme.govBlue }} />
            <div style={{ fontSize: '14px', fontWeight: '700', color: theme.govBlue }}>
              ELECTRONICALLY GENERATED RECEIPT
            </div>
          </div>
          <div style={{
            fontSize: '13px',
            color: theme.gray700,
            lineHeight: '1.6'
          }}>
            This is a computer-generated receipt and does not require any physical signature. 
            The digital payment confirmation serves as valid proof of booking.
          </div>
        </div>

        {/* Terms & Conditions */}
        <div>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '800',
            color: theme.gray900,
            marginBottom: '20px',
            borderBottom: `3px solid ${theme.govBlue}`,
            paddingBottom: '12px'
          }}>
            Terms & Conditions
          </h2>

          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: `2px solid ${theme.gray900}`,
            marginBottom: '24px'
          }}>
            <thead>
              <tr>
                <th style={{
                  background: theme.gray900,
                  color: theme.white,
                  padding: '12px',
                  textAlign: 'left',
                  fontSize: '14px',
                  fontWeight: '700'
                }}>
                  Terms & Condition
                </th>
              </tr>
            </thead>
            <tbody>
              {getTermsAndConditions(receiptData.venueType).map((term, index) => (
                <tr key={index}>
                  <td style={{
                    padding: '12px',
                    borderTop: `1px solid ${theme.gray200}`,
                    fontSize: '13px',
                    lineHeight: '1.6',
                    color: theme.gray900
                  }}>
                    {index + 1}. {term}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Additional Notes */}
          <div style={{
            background: `${theme.govBlue}10`,
            border: `2px solid ${theme.govBlue}40`,
            borderRadius: '8px',
            padding: '16px',
            marginTop: '20px'
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '700',
              color: theme.gray900,
              lineHeight: '1.8'
            }}>
              <div style={{ marginBottom: '8px' }}>
                NOTE: There is no permission for cancellation of event/programme, if once booked.
              </div>
              <div style={{ marginBottom: '8px' }}>
                NOTE: You should physically check the venue before the event date.
              </div>
              <div>
                NOTE: Security deposit will be refunded after successful event completion.
              </div>
            </div>
          </div>
        </div>
      </div>


      

      {/* Footer */}
      <div style={{
        background: theme.gray900,
        color: theme.white,
        padding: '20px 40px',
        textAlign: 'center',
        fontSize: '12px'
      }}>
        <div style={{ marginBottom: '8px' }}>
          Generated on: {receiptData.bookingDate}
        </div>
        <div>
          © 2025 Hyderabad Metropolitan Development Authority. All rights reserved.
        </div>
        <div style={{ marginTop: '8px', opacity: 0.8 }}>
          https://bpp.hmda.gov.in
        </div>
      </div>
    </div>
  </div>
)}


    </div>
  );
};

export default HMDAPortal;